import { inject, Injectable } from "@angular/core";
import { GasService } from "@domain/gas/gas.service";
import { Relationship, RelationshipType } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { FlameDetectorConfiguration } from "@domain/project/configurations/flame-detector-configuration";
import { FgdsProject } from "@domain/project/fgds-project";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { ProductItemGroup } from "@domain/project/floorplan/product-item-group";
import { GroupItems } from "@domain/project/product-list/group-items";
import { ScopedProject } from "@domain/project/project-guards";
import { forkJoin, map, Observable, of } from "rxjs";

@Injectable({ providedIn: "root" })
export class ProductConfigurationService {
  private productService = inject(ProductService);
  private gasService = inject(GasService);

  public getIncompleteConfigIdsForItems(items: GroupItems): Observable<string[]> {
    const incompleteFlameDetectorIds = this.getIncompleteFlameDetectorIds(items.flameDetectors.map((fd) => fd.config));
    return this.getIncompleteConfigIdsForTransmitters(items.transmitters.map((t) => t.config)).pipe(
      map((transmitterIds) => [...incompleteFlameDetectorIds, ...transmitterIds]),
    );
  }

  public getIncompleteConfigIdsInGroup(group: ProductItemGroup, project: ScopedProject): Observable<string[]> {
    const transmitters = group.references.flatMap((ref) => {
      const floorplan = project.getFloorplanById(ref.floorplanId);
      if (!floorplan) return [];
      return [...floorplan.transmitters].filter((t): t is FloorplanTransmitter => t.id === ref.floorplanProductItemId);
    });
    const flameDetectors = group.references.flatMap((ref) => {
      const floorplan = project.getFloorplanById(ref.floorplanId);
      if (!floorplan) return [];
      return [...floorplan.flameDetectors].filter((fd): fd is FloorplanFlameDetector => fd.id === ref.floorplanProductItemId);
    });
    const incompleteFlameDetectorIds = this.getIncompleteFlameDetectorIds(flameDetectors.map((fd) => fd.config));
    return this.getIncompleteConfigIdsForTransmitters(transmitters.map((t) => t.config)).pipe(
      map((transmitterIds) => [...incompleteFlameDetectorIds, ...transmitterIds]),
    );
  }

  private getIncompleteConfigIdsForTransmitters(
    transmitterConfigs: { id: string; gasId?: string; sensorId?: string; productId: string }[],
  ): Observable<string[]> {
    const transmitters$ = transmitterConfigs.map((config) => {
      if (!config.gasId) return of(config.id);
      if (config.sensorId) return of(null);
      const product$ = this.productService.getProductById(config.productId);
      const gas$ = this.gasService.getGasById(config.gasId!);
      return forkJoin([product$, gas$]).pipe(
        map(([product, gas]) => {
          if (!gas || !product) return config.id;
          const needsSensor = product.relationships
            .filter((r: Relationship) => r.relationshipType === RelationshipType.SENSOR)
            .some((sensor) => gas.productSensors.some((gasSensor) => gasSensor.productId === sensor.productId));
          return needsSensor ? config.id : null;
        }),
      );
    });
    const incomplete$ = transmitters$.filter((obs) => obs !== null);
    if (incomplete$.length === 0) return of([]);
    return forkJoin(incomplete$).pipe(map((results) => results.filter((id) => id !== null) as string[]));
  }

  public getIncompleteConfigIds(project: FgdsProject): Observable<string[]> {
    const incompleteFlameDetectorIds = this.getIncompleteFlameDetectorIds(project.flameDetectors);

    const transmitterObservables$ = project.transmitters.map((transmitterConfiguration) => {
      if (!transmitterConfiguration.gasId) {
        return of(transmitterConfiguration.id);
      }
      if (transmitterConfiguration.sensorId) {
        return of(null);
      }
      const product$ = this.productService.getProductById(transmitterConfiguration.productId);
      const gas$ = this.gasService.getGasById(transmitterConfiguration.gasId!);
      return forkJoin([product$, gas$]).pipe(
        map(([product, gas]) => {
          if (!gas || !product) {
            return transmitterConfiguration.id;
          }
          const needsSensor = product.relationships
            .filter((relationship: Relationship) => relationship.relationshipType === RelationshipType.SENSOR)
            .some((sensor) => gas.productSensors.some((gasSensor) => gasSensor.productId === sensor.productId));
          return needsSensor ? transmitterConfiguration.id : null;
        }),
      );
    });
    const incompleteTransmitterObservables$ = transmitterObservables$.filter((obs) => obs !== null);
    if (incompleteTransmitterObservables$.length === 0) {
      return of(incompleteFlameDetectorIds);
    }
    return forkJoin(incompleteTransmitterObservables$).pipe(
      map((results) => {
        return [...incompleteFlameDetectorIds, ...(results.filter((id) => id !== null) as string[])];
      }),
    );
  }

  private getIncompleteFlameDetectorIds(flameDetectors: readonly FlameDetectorConfiguration[]): string[] {
    return flameDetectors.filter((fd) => !fd.substanceId).map((fd) => fd.id);
  }
}
